import { useEffect, useState, type SVGProps } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { customersApi } from "../lib/api";
import { formatDate } from "../lib/money";
import { Spinner } from "../components/ui-modern";
import {
  AvatarBadge,
  Eyebrow,
  ModernButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernPagination,
  ModernSearchInput,
  ModernSelect,
  ModernTableCard,
  ModernTd,
  ModernTh,
} from "../components/ui-modern";

/** Same initials rule AppShellModern.tsx's own LogoutRow uses for the
 * signed-in user's avatar — first letter of up to the first two
 * whitespace-separated words, uppercased. Duplicated rather than shared
 * since that one lives inline in a component that isn't exported. */
function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

function GridIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
    </svg>
  );
}
function TableIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 10v10" />
    </svg>
  );
}

type SortColumn = "createdAt" | "displayName";
type SortDirection = "asc" | "desc";
interface SortState {
  column: SortColumn;
  direction: SortDirection;
}
// Recently added first, per the owner's ask — matches the API's own
// default (CustomersService.list(), unset sortBy) so this constant and the
// server never disagree about what "default" means.
const DEFAULT_SORT: SortState = { column: "createdAt", direction: "desc" };

// One combined picker rather than separate column/direction controls (the
// grid layout below has no column headers to click, unlike
// WorkOrdersListPageModern's sortable `ModernTh`s) — each option is a
// human-readable label for one {column, direction} pair.
const SORT_OPTIONS: { value: string; label: string; sort: SortState }[] = [
  { value: "recent", label: "Recently added", sort: { column: "createdAt", direction: "desc" } },
  { value: "oldest", label: "Oldest first", sort: { column: "createdAt", direction: "asc" } },
  { value: "name-asc", label: "Name (A–Z)", sort: { column: "displayName", direction: "asc" } },
  { value: "name-desc", label: "Name (Z–A)", sort: { column: "displayName", direction: "desc" } },
];
function sortOptionValue(sort: SortState): string {
  return SORT_OPTIONS.find((o) => o.sort.column === sort.column && o.sort.direction === sort.direction)?.value ?? "recent";
}

type Layout = "grid" | "table";
// Same localStorage-preference pattern as AppShellModern.tsx's own content-
// zoom setting — a per-browser display convenience, not data, so it's never
// sent to the API.
const LAYOUT_STORAGE_KEY = "b2b-customers-layout";
function readStoredLayout(): Layout {
  return localStorage.getItem(LAYOUT_STORAGE_KEY) === "table" ? "table" : "grid";
}

/**
 * The modern theme's Customers list — see docs/FRONTEND_BACKLOG.md
 * Round 60 and `Bay2Bill Redesign.dc.html` lines ~539-574 ("Customers").
 *
 * Same data as CustomersListPageClassic.tsx — identical query key/params,
 * duplicated here as a thin re-run of the same `useQuery` (see
 * WorkOrdersListPageModern.tsx's own comment on why duplication over
 * extraction for a page with no mutations of its own).
 *
 * The mockup's card grid has a bottom row of "{jobs} jobs" + a colored
 * balance-due figure per customer. `customersApi.list` (Classic's own data
 * source, unchanged here) returns `CustomerListItem` — id, contact fields,
 * phones, and each active vehicle's year/make — with no job count or
 * balance field at all (that data lives on WorkOrder/Invoice rows, joined
 * per-customer only on the Customer DETAIL page's own aggregate query, out
 * of scope for this round's list-screen work and NOT something to fabricate
 * here). Rather than invent numbers the API doesn't provide, this drops
 * that bottom row entirely and shows only what Classic already shows
 * (name, customer #, phone, vehicle chips) — flagged here as a follow-up:
 * a real per-customer jobs-count/balance-due needs a `ListCustomersResult`
 * shape change, matching DashboardPageModern's own documented data
 * substitution for its job board/invoice list.
 *
 * Added a sort picker (default: recently added first) and a grid/table
 * layout toggle — the grid is this page's original card layout, unchanged;
 * table is a new, denser row layout for the same data. The layout choice
 * (not the sort/search/page) persists across visits via localStorage.
 */
export function CustomersListPageModern() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [layout, setLayout] = useState<Layout>(readStoredLayout);

  useEffect(() => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  }, [layout]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["customers", search, sort, page, pageSize],
    queryFn: () =>
      customersApi.list({
        search: search || undefined,
        sortBy: sort.column,
        sortDirection: sort.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  const pagination = data && (
    <ModernPagination
      bordered={layout === "grid" ? false : undefined}
      page={page}
      pageSize={pageSize}
      total={data.total}
      onPageChange={setPage}
      onPageSizeChange={(n) => {
        setPageSize(n);
        setPage(1);
      }}
    />
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">{data ? `${data.total} on file` : "…"}</Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Customers</h1>
        </div>
        <Link to="/customers/new">
          <ModernButton>+ New customer</ModernButton>
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <ModernSearchInput
          className="max-w-[340px] flex-1"
          placeholder="Name, number, or phone…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Search customers"
        />
        <div className="w-[190px] flex-none">
          <ModernSelect
            aria-label="Sort customers"
            value={sortOptionValue(sort)}
            onChange={(e) => {
              const opt = SORT_OPTIONS.find((o) => o.value === e.target.value);
              if (opt) {
                setSort(opt.sort);
                setPage(1);
              }
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </ModernSelect>
        </div>
        <div className="flex flex-none items-center gap-1 rounded-[10px] border border-[#e2e7ee] p-1" role="group" aria-label="Layout">
          <button
            type="button"
            title="Grid view"
            aria-label="Grid view"
            aria-pressed={layout === "grid"}
            onClick={() => setLayout("grid")}
            className={`flex h-9 w-9 items-center justify-center rounded-[8px] ${
              layout === "grid" ? "bg-[#e6f1f5] text-[#0f6d86]" : "text-[#8b97a8] hover:bg-[#f1f4f9]"
            }`}
          >
            <GridIcon />
          </button>
          <button
            type="button"
            title="Table view"
            aria-label="Table view"
            aria-pressed={layout === "table"}
            onClick={() => setLayout("table")}
            className={`flex h-9 w-9 items-center justify-center rounded-[8px] ${
              layout === "table" ? "bg-[#e6f1f5] text-[#0f6d86]" : "text-[#8b97a8] hover:bg-[#f1f4f9]"
            }`}
          >
            <TableIcon />
          </button>
        </div>
      </div>

      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message="Could not load customers." />}
      {data && data.items.length === 0 && (
        <ModernEmptyState>No customers found. Try a different search, or add a new one.</ModernEmptyState>
      )}

      {data && data.items.length > 0 && layout === "table" && (
        <ModernTableCard>
          <table className="w-full min-w-[780px] border-collapse text-[14px]">
            <thead>
              <tr className="bg-[#f8fafc]">
                <ModernTh>Customer</ModernTh>
                <ModernTh>Phone</ModernTh>
                <ModernTh>Email</ModernTh>
                <ModernTh>Vehicles</ModernTh>
                <ModernTh>Added</ModernTh>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/customers/${c.customerNumber}`)}
                  className="cursor-pointer transition-colors hover:bg-[#f1f7f9]"
                >
                  <ModernTd>
                    <div className="flex items-center gap-2.5">
                      <AvatarBadge initials={initialsOf(c.displayName)} size={32} fontSize={12} />
                      <span className="min-w-0">
                        <Link
                          to={`/customers/${c.customerNumber}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block truncate font-semibold text-[#071a33] no-underline"
                        >
                          {c.displayName}
                        </Link>
                        <span
                          className="block truncate font-mono text-[11.5px] leading-tight text-[#8b97a8]"
                          style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                        >
                          #{c.customerNumber}
                        </span>
                      </span>
                    </div>
                  </ModernTd>
                  <ModernTd className="text-[13.5px] text-[#101b2d]">{c.phones[0]?.phoneNumber ?? "—"}</ModernTd>
                  <ModernTd className="text-[13.5px] text-[#101b2d]">{c.email ?? "—"}</ModernTd>
                  <ModernTd>
                    {c.vehicles.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {c.vehicles.map((v) => (
                          <span key={v.id} className="rounded-[6px] bg-[#f1f4f9] px-[9px] py-1 text-[12px] font-semibold text-[#4a5361]">
                            {[v.modelYear, v.make].filter(Boolean).join(" ") || "Vehicle"}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[13.5px] text-[#8b97a8]">—</span>
                    )}
                  </ModernTd>
                  <ModernTd className="text-[13px] text-[#6b7a8f]">{formatDate(c.createdAt)}</ModernTd>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination}
        </ModernTableCard>
      )}

      {data && data.items.length > 0 && layout === "grid" && (
        <>
          <div className="grid grid-cols-1 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(250px,1fr))]">
            {data.items.map((c) => (
              <Link
                key={c.id}
                to={`/customers/${c.customerNumber}`}
                className="block rounded-[14px] border border-[#e2e7ee] bg-white p-[17px] no-underline transition-colors hover:border-[#0f6d86]"
              >
                <div className="flex items-start gap-2.5">
                  <AvatarBadge initials={initialsOf(c.displayName)} size={38} fontSize={13} />
                  <span className="min-w-0">
                    <span className="block truncate text-[15px] font-bold text-[#071a33]">{c.displayName}</span>
                    <span
                      className="block truncate font-mono text-[11.5px] leading-tight text-[#8b97a8]"
                      style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                    >
                      #{c.customerNumber}
                      {c.phones[0] ? ` · ${c.phones[0].phoneNumber}` : ""}
                    </span>
                    {c.email && <span className="block truncate text-[12.5px] text-[#6b7a8f]">{c.email}</span>}
                  </span>
                </div>
                {c.vehicles.length > 0 && (
                  <div className="mt-3.5 flex flex-wrap gap-1.5">
                    {c.vehicles.map((v) => (
                      <span key={v.id} className="rounded-[6px] bg-[#f1f4f9] px-[9px] py-1 text-[12px] font-semibold text-[#4a5361]">
                        {[v.modelYear, v.make].filter(Boolean).join(" ") || "Vehicle"}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
          {pagination}
        </>
      )}
    </div>
  );
}
